import React from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { adminStatsAPI } from '../services/adminApi';
import { setAdminNavHandoff } from '../utils/adminNavFilters';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const BRAND = { blue: '#043663', red: '#D52B1E', muted: '#64748b', amber: '#b45309' };

const Page = styled.div`
  max-width: 1100px;
`;

const Header = styled.div`
  margin-bottom: 1.25rem;
  h2 {
    margin: 0 0 0.35rem;
    font-size: 1.65rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  p {
    margin: 0;
    color: ${BRAND.muted};
    font-size: 0.9rem;
    max-width: 44rem;
    line-height: 1.45;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1rem;
`;

const Field = styled.div`
  label {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin-bottom: 0.2rem;
  }
  select {
    min-width: 180px;
    padding: 0.45rem 0.65rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 0.875rem;
    background: #fff;
  }
`;

const Note = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  font-size: 0.8125rem;
  color: #075985;
  line-height: 1.45;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.85rem;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${(p) => (p.$hot ? '#fecaca' : '#e2e8f0')};
  border-radius: 12px;
  padding: 1rem 1.1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  .count {
    font-size: 1.75rem;
    font-weight: 800;
    color: ${(p) => (p.$hot ? BRAND.red : BRAND.blue)};
    line-height: 1.1;
  }
  .title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
  }
  .desc {
    font-size: 0.78rem;
    color: ${BRAND.muted};
    line-height: 1.4;
    flex: 1;
  }
  .partial {
    font-size: 0.7rem;
    color: ${BRAND.amber};
    font-weight: 600;
  }
`;

const OpenBtn = styled.button`
  align-self: flex-start;
  margin-top: 0.25rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  border: none;
  background: ${BRAND.blue};
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #032a4a;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${BRAND.muted};
`;

/**
 * @param {{ onNavigate: (section: string, filters?: object) => void }} props
 */
export default function AdminAttentionCenter({ onNavigate }) {
  const {
    campaignId,
    seasonId,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allowAll,
  } = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
  });

  const { data, isLoading, error } = useQuery(
    ['admin-attention-center', campaignId, seasonId],
    () =>
      adminStatsAPI.getLeagueAnalytics({
        seasonId: seasonId || undefined,
      }),
    { keepPreviousData: true }
  );

  const payload = data?.data || data || null;
  const k = payload?.kpis || {};
  const d = payload?.disclaimers || {};

  const go = (section, filters = {}) => {
    const handoff = {
      section,
      seasonId: seasonId || filters.seasonId || undefined,
      ...filters,
    };
    setAdminNavHandoff(handoff);
    if (typeof onNavigate === 'function') {
      onNavigate(section, handoff);
    }
  };

  const cards = [
    {
      id: 'withdrawals',
      title: 'Withdrawals / inactive seats',
      count: k.withdrawnOrInactiveTeams ?? 0,
      desc: d.withdrawals || 'Team-phase rows with is_active = false.',
      partial: true,
      hot: (k.withdrawnOrInactiveTeams || 0) > 0,
      action: () =>
        go('teams', {
          isActive: 'false',
          unassignedDivision: false,
        }),
      actionLabel: 'Open Teams (inactive)',
    },
    {
      id: 'pending',
      title: 'Incomplete registrations',
      count: k.registrationsPending ?? 0,
      desc: 'Pending payment registration rows (TSR). Also check Orders for unpaid invoices.',
      hot: (k.registrationsPending || 0) > 0,
      action: () => go('analytics', { analyticsTab: 'ops' }),
      actionLabel: 'Open Analytics → Attention',
      secondary: () => go('registration-orders', { registrationOrdersStatus: 'pending' }),
      secondaryLabel: 'Open Orders (pending)',
    },
    {
      id: 'remaining',
      title: 'Remaining matches',
      count: k.remainingMatches ?? 0,
      desc: 'Matches not completed / cancelled / forfeited / walkover.',
      hot: (k.remainingMatches || 0) > 0,
      action: () =>
        go('matches', {
          matchStatus: 'scheduled',
        }),
      actionLabel: 'Open Matches (scheduled)',
    },
    {
      id: 'forfeits',
      title: 'Forfeit matches',
      count: k.forfeitMatchesHeuristic ?? 0,
      desc: d.forfeitMatches || 'Heuristic count from notes / score patterns.',
      partial: true,
      hot: (k.forfeitMatchesHeuristic || 0) > 0,
      action: () => go('analytics', { analyticsTab: 'ops' }),
      actionLabel: 'Open Analytics → Attention',
    },
    {
      id: 'unassigned',
      title: 'Unassigned teams',
      count: k.unassignedTeams ?? 0,
      desc: 'Teams without a division assignment in scope.',
      hot: (k.unassignedTeams || 0) > 0,
      action: () =>
        go('teams', {
          unassignedDivision: true,
          isActive: 'all',
        }),
      actionLabel: 'Open Teams (unassigned)',
    },
    {
      id: 'disputes',
      title: 'Open disputes',
      count: k.openDisputes ?? 0,
      desc: 'Score confirmations in disputed or pending status.',
      hot: (k.openDisputes || 0) > 0,
      action: () =>
        go('matches', {
          matchStatus: 'disputed',
        }),
      actionLabel: 'Open Matches (disputed)',
    },
    {
      id: 'invites',
      title: 'Pending invitations',
      count: k.pendingInvitations ?? 0,
      desc: 'Outstanding partner / team invitations in this tenant.',
      hot: (k.pendingInvitations || 0) > 0,
      action: () => go('analytics', { analyticsTab: 'ops' }),
      actionLabel: 'Open Analytics → Attention',
    },
    {
      id: 'stats',
      title: 'League overview',
      count: k.registrationsPaid ?? 0,
      desc: 'Paid registrations in the current phase filter — jump to League Statistics.',
      hot: false,
      action: () => go('league-statistics', {}),
      actionLabel: 'Open League Statistics',
    },
  ];

  return (
    <Page>
      <Header>
        <h2>Attention</h2>
        <p>
          Issues that usually need admin action. Opening a card jumps into an existing screen with filters
          pre-applied — nothing here replaces Teams, Matches, or Analytics.
        </p>
      </Header>

      <FilterBar>
        <AdminSeasonPhaseFilters
          campaignId={campaignId}
          seasonId={seasonId}
          seasonOptions={seasonOptions}
          phaseOptions={phaseOptions}
          onCampaignChange={onCampaignChange}
          onPhaseChange={onPhaseChange}
          allowAll={allowAll}
        />
      </FilterBar>

      <Note>
        Counts come from the same league-analytics API as League Statistics. Partial metrics are labeled.
        Use phase scope to focus KPIs; deep-links carry that phase where the target screen supports it.
      </Note>

      {isLoading && !payload ? (
        <Empty>Loading attention items…</Empty>
      ) : error ? (
        <Empty style={{ color: BRAND.red }}>
          {error?.response?.data?.message || error?.message || 'Failed to load'}
        </Empty>
      ) : (
        <Grid>
          {cards.map((c) => (
            <Card key={c.id} $hot={c.hot}>
              <div className="count">{Number(c.count).toLocaleString()}</div>
              <div className="title">{c.title}</div>
              {c.partial ? <div className="partial">Partial / proxy</div> : null}
              <div className="desc">{c.desc}</div>
              <OpenBtn type="button" onClick={c.action}>
                {c.actionLabel}
              </OpenBtn>
              {c.secondary ? (
                <OpenBtn
                  type="button"
                  onClick={c.secondary}
                  style={{ background: '#fff', color: BRAND.blue, border: `1px solid ${BRAND.blue}` }}
                >
                  {c.secondaryLabel}
                </OpenBtn>
              ) : null}
            </Card>
          ))}
        </Grid>
      )}
    </Page>
  );
}
